import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { updateTruckVehicleScheme } from "../../../validations/createVehicleScheme";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Divider,
  IconButton,
  ImageListItem,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import { getDate, getVehicleType } from "../../../utils";
import VehiclePlaceholderImage from "../../../assets/images/vehicle_placeholder.jpeg";
import { ReactComponent as UploadIcon } from "../../../assets/svg/uploaddoc.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/svg/deleteFile.svg";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const UpdateTruckForm = ({
  submitForm,
  selectedVehicle,
  disableSubmit = false,
}) => {
  const [uploadTruckFile, setUploadTruckFile] = useState(null);
  const [uploadTruckImage, setUploadTruckImage] = useState(null);

  const [uploadLicensePlate, setUploadLicensePlate] = useState(null);
  const [uploadLicensePlateImage, setUploadLicensePlateImage] = useState(null);
  const [isLicensePlateRemoved, setIsLicensePlateRemoved] = useState(false);
  const [isTruckFileRemoved, setIsTruckFileRemoved] = useState(false);
  const [uploadInsuranceFile, setUploadInsuranceFile] = useState(null);
  const [uploadInsuranceImage, setUploadInsuranceImage] = useState(null);
  const [isInsuranceRemoved, setIsInsuranceRemoved] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: "all",
    defaultValues: selectedVehicle,
    resolver: yupResolver(updateTruckVehicleScheme),
  });

  const handleImageUploadChange = (e) => {
    const uploadedFile = URL.createObjectURL(e.target.files[0]);
    setUploadTruckImage(uploadedFile);
    setUploadTruckFile(e.target.files[0]);
  };
  const handleLicensePlateImageUploadChange = (e) => {
    const uploadedFile = URL.createObjectURL(e.target.files[0]);
    setUploadLicensePlate(e.target.files[0]);
    setUploadLicensePlateImage(uploadedFile);
  };
  const handleInsuranceUploadChange = (e) => {
    const uploadedFile = URL.createObjectURL(e.target.files[0]);
    setUploadInsuranceFile(e.target.files[0]);
    setUploadInsuranceImage(uploadedFile);
  };
  const fTruckMedia = selectedVehicle?.fileUploadResponse
    ? selectedVehicle?.fileUploadResponse?.filter(
        (fmt) => fmt.mediaCode === "VEHCLPIC",
      )[0]
    : "";

  const fTruckLicensePlate = selectedVehicle?.fileUploadResponse
    ? selectedVehicle?.fileUploadResponse?.filter(
        (fmt) => fmt.mediaCode === "LICPL",
      )[0]
    : "";
  const fTruckInsurance = selectedVehicle?.fileUploadResponse
    ? selectedVehicle?.fileUploadResponse?.filter(
        (fmt) => fmt.mediaCode === "PROOFINS",
      )[0]
    : "";

  return (
    <form
      onSubmit={handleSubmit((data) => {
        if (!uploadInsuranceFile) {
          setErrorMessage(t("components.updateTruckForm.text05"));
          return;
        }

        submitForm(
          data,
          isTruckFileRemoved,
          isLicensePlateRemoved,
          isInsuranceRemoved,
          uploadTruckFile,
          uploadLicensePlate,
          uploadInsuranceFile,
        );
        reset({});
      })}
    >
      <Stack spacing={2}>
        <Controller
          name="vehicleType"
          control={control}
          disabled={true}
          render={({ field }) => (
            <TextField
              {...field}
              name="vehicleType"
              type="text"
              id="standard-required"
              label={t("pages.addVehicle.text01")}
              defaultValue={getVehicleType(selectedVehicle?.veh_type_cd)}
              variant="standard"
              className="edit_field"
              size="small"
              margin="none"
              sx={{
                "& .MuiStack-root": {
                  paddingTop: "20% !important",
                },
                fontFamily: "Segoe UI",
                fontSize: 15,
                fontWeight: 400,
                textAlign: "right",
                color: "#1D5AB9",
              }}
              error={!!errors.vehicleType}
              helperText={errors.vehicleType?.message}
            />
          )}
        ></Controller>
        <Controller
          name="dotNumber"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              name="dotNumber"
              type="text"
              id="standard-required"
              label={t("pages.addVehicle.text02")}
              defaultValue={selectedVehicle?.dotNumber}
              variant="standard"
              className="edit_field"
              size="small"
              margin="none"
              sx={{
                "& .MuiStack-root": {
                  paddingTop: "20% !important",
                },
              }}
              error={!!errors.dotNumber}
              helperText={errors.dotNumber?.message}
            />
          )}
        ></Controller>
        <Controller
          name="make"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              name="make"
              type="text"
              id="standard-required"
              label={t("pages.addVehicle.text03")}
              defaultValue={selectedVehicle?.make}
              variant="standard"
              className="edit_field"
              size="small"
              margin="none"
              sx={{
                "& .MuiStack-root": {
                  paddingTop: "20% !important",
                },
              }}
              error={!!errors.make}
              helperText={errors.make?.message}
            />
          )}
        ></Controller>
        <Controller
          name="model"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              name="model"
              type="text"
              id="standard-required"
              label={t("pages.addVehicle.text05")}
              defaultValue={selectedVehicle?.model}
              variant="standard"
              className="edit_field"
              size="small"
              margin="none"
              sx={{
                "& .MuiStack-root": {
                  paddingTop: "20% !important",
                },
              }}
              error={!!errors.model}
              helperText={errors.model?.message}
            />
          )}
        ></Controller>
        <Controller
          name="color"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              name="color"
              type="text"
              id="standard-required"
              label={t("pages.addVehicle.text04")}
              defaultValue={selectedVehicle?.color}
              variant="standard"
              className="edit_field"
              size="small"
              margin="none"
              sx={{
                "& .MuiStack-root": {
                  paddingTop: "20% !important",
                },
              }}
              error={!!errors.color}
              helperText={errors.color?.message}
            />
          )}
        ></Controller>
        <Controller
          name="plateNumber"
          control={control}
          disabled={true}
          render={({ field }) => (
            <TextField
              {...field}
              name="plateNumber"
              type="text"
              id="standard-required"
              label={t("pages.addVehicle.text06")}
              defaultValue={selectedVehicle?.lic_plate_num}
              variant="standard"
              className="edit_field"
              size="small"
              margin="none"
              sx={{
                "& .MuiStack-root": {
                  paddingTop: "20% !important",
                },
              }}
              error={!!errors.plateNumber}
              helperText={errors.plateNumber?.message}
            />
          )}
        ></Controller>
        {errorMessage && <Typography color="error">{errorMessage}</Typography>}
        <div style={{ alignItems: "center", flexDirection: "row" }}>
          <div
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ListItem alignItems="center" sx={{ paddingLeft: 0 }}>
              <ImageListItem>
                <img
                  alt=""
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 8,
                  }}
                  loading="lazy"
                  src={
                    uploadTruckImage
                      ? uploadTruckImage
                      : fTruckMedia?.tempUrlTumbs
                        ? isTruckFileRemoved
                          ? VehiclePlaceholderImage
                          : fTruckMedia?.tempUrlTumbs
                        : VehiclePlaceholderImage
                  }
                />
              </ImageListItem>
              <ListItemText
                primary={
                  <div
                    style={{
                      flexDirection: "row",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      color="text.primary"
                      style={{
                        fontFamily: "Segoe UI",
                        fontSize: 16,
                        fontWeight: 600,
                        textAlign: "left",
                        letterSpacing: "0.5%",
                      }}
                    >
                      {t("components.updateTruckForm.text01")}
                    </Typography>
                  </div>
                }
                style={{
                  fontFamily: "Segoe UI",
                  fontSize: 16,
                  fontWeight: 600,
                  textAlign: "left",
                  letterSpacing: "0.5%",
                }}
                sx={{ marginLeft: 2 }}
                secondary={
                  <React.Fragment>
                    {fTruckMedia?.mediaUpdDate && (
                      <Typography
                        sx={{ display: "inline" }}
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        {`${t("components.updateTruckForm.text02")} ${getDate(fTruckMedia?.mediaUpdDate)}`}
                      </Typography>
                    )}
                  </React.Fragment>
                }
              />
            </ListItem>

            <div
              style={{
                flexDirection: "row",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Tooltip title="Upload" arrow>
                <IconButton
                  aria-label="upload"
                  component="label"
                  sx={{ padding: 0 }}
                >
                  <>
                    <UploadIcon style={{ width: 40, height: 40 }} />
                    <VisuallyHiddenInput
                      type="file"
                      onChange={handleImageUploadChange}
                      accept="image/png, image/jpeg, application/pdf"
                    />
                  </>
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove" arrow>
                <IconButton
                  aria-label="upload"
                  component="label"
                  onClick={() => {
                    setUploadTruckFile(null);
                    setUploadTruckImage(null);
                    setIsTruckFileRemoved(true);
                  }}
                  sx={{ padding: 0 }}
                >
                  <DeleteIcon style={{ width: 40, height: 40 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <Divider
            variant="fullWidth"
            color="lightGray"
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ alignItems: "center", flexDirection: "row" }}>
          <div
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ListItem alignItems="center" sx={{ paddingLeft: 0 }}>
              <ImageListItem>
                <img
                  alt=""
                  style={{
                    width: 60,
                    height: 60,
                    top: 12,
                    borderRadius: 8,
                  }}
                  loading="lazy"
                  src={
                    uploadLicensePlateImage
                      ? uploadLicensePlateImage
                      : fTruckLicensePlate?.tempUrlTumbs
                        ? isLicensePlateRemoved
                          ? VehiclePlaceholderImage
                          : fTruckLicensePlate?.tempUrlTumbs
                        : VehiclePlaceholderImage
                  }
                />
              </ImageListItem>
              <ListItemText
                primary={
                  <div
                    style={{
                      flexDirection: "row",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      color="text.primary"
                      style={{
                        fontFamily: "Segoe UI",
                        fontSize: 16,
                        fontWeight: 600,
                        textAlign: "left",
                        letterSpacing: "0.5%",
                      }}
                    >
                      {t("components.updateTruckForm.text03")}
                    </Typography>
                  </div>
                }
                style={{
                  fontFamily: "Segoe UI",
                  fontSize: 16,
                  fontWeight: 600,
                  textAlign: "left",
                  letterSpacing: "0.5%",
                }}
                sx={{ marginLeft: 2 }}
                secondary={
                  <React.Fragment>
                    {fTruckLicensePlate?.mediaUpdDate && (
                      <Typography
                        sx={{ display: "inline" }}
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        {`${t("components.updateTruckForm.text02")} ${getDate(
                          fTruckLicensePlate?.mediaUpdDate,
                        )}`}
                      </Typography>
                    )}
                  </React.Fragment>
                }
              />
            </ListItem>

            <div style={{ flexDirection: "row", display: "flex" }}>
              <Tooltip title="Upload" arrow>
                <IconButton
                  aria-label="upload"
                  component="label"
                  sx={{ padding: 0 }}
                >
                  <>
                    <UploadIcon style={{ width: 40, height: 40 }} />
                    <VisuallyHiddenInput
                      type="file"
                      onChange={handleLicensePlateImageUploadChange}
                      accept="image/png, image/jpeg, application/pdf"
                    />
                  </>
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove" arrow>
                <IconButton
                  aria-label="upload"
                  component="label"
                  onClick={() => {
                    setUploadLicensePlate(null);
                    setUploadLicensePlateImage(null);
                    setIsLicensePlateRemoved(true);
                  }}
                  sx={{ padding: 0 }}
                >
                  <DeleteIcon style={{ width: 40, height: 40 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <Divider
            variant="fullWidth"
            color="lightGray"
            style={{ width: "100%" }}
          />
        </div>
        <div style={{ alignItems: "center", flexDirection: "row" }}>
          <div
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ListItem alignItems="center" sx={{ paddingLeft: 0 }}>
              <ImageListItem>
                <img
                  alt=""
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 8,
                  }}
                  loading="lazy"
                  src={
                    uploadInsuranceImage
                      ? uploadInsuranceImage
                      : fTruckInsurance?.tempUrlTumbs
                        ? isInsuranceRemoved
                          ? VehiclePlaceholderImage
                          : fTruckInsurance?.tempUrlTumbs
                        : VehiclePlaceholderImage
                  }
                />
              </ImageListItem>

              <ListItemText
                primary={
                  <div
                    style={{
                      flexDirection: "row",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      color="text.primary"
                      style={{
                        fontFamily: "Segoe UI",
                        fontSize: 16,
                        fontWeight: 600,
                        textAlign: "left",
                        letterSpacing: "0.5%",
                      }}
                    >
                      {t("components.updateTruckForm.text04")}
                    </Typography>
                  </div>
                }
                style={{
                  fontFamily: "Segoe UI",
                  fontSize: 16,
                  fontWeight: 600,
                  textAlign: "left",
                  letterSpacing: "0.5%",
                }}
                sx={{ marginLeft: 2 }}
                secondary={
                  <React.Fragment>
                    {fTruckInsurance?.mediaUpdDate && (
                      <Typography
                        sx={{ display: "inline" }}
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        {`${t("components.updateTruckForm.text02")} ${getDate(
                          fTruckInsurance?.mediaUpdDate,
                        )}`}
                      </Typography>
                    )}
                  </React.Fragment>
                }
              />
            </ListItem>

            <div style={{ flexDirection: "row", display: "flex" }}>
              <Tooltip title="Upload" arrow>
                <IconButton
                  aria-label="upload"
                  component="label"
                  sx={{ padding: 0 }}
                >
                  <>
                    <UploadIcon style={{ width: 40, height: 40 }} />
                    <VisuallyHiddenInput
                      type="file"
                      onChange={handleInsuranceUploadChange}
                      accept="image/png, image/jpeg, application/pdf"
                    />
                  </>
                </IconButton>
              </Tooltip>

              <Tooltip title="Remove" arrow>
                <IconButton
                  aria-label="upload"
                  component="label"
                  onClick={() => {
                    setUploadInsuranceFile(null);
                    setUploadInsuranceImage(null);
                    setIsInsuranceRemoved(true);
                  }}
                  sx={{ padding: 0 }}
                >
                  <DeleteIcon style={{ width: 40, height: 40 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <Divider
            variant="fullWidth"
            color="lightGray"
            style={{ width: "100%" }}
          />
        </div>
        <Button
          type="submit"
          variant="contained"
          alignSelf={"center"}
          disabled={disableSubmit || !isValid}
          style={{
            alignSelf: "center",
            width: 199,
            height: 32,
            marginLeft: 88,
            alignItems: "center",
            margin: "auto",
            // marginTop: "10%",
            // marginBottom: "5%",
            marginTop: 50,
            display: "flex",
          }}
          className="update_button"
          onClick={() => null}
        >
          <Typography variant="buttonText" color="common.buttonText">
            {t("commonLabels.text16")}
          </Typography>
        </Button>
      </Stack>
    </form>
  );
};

export default UpdateTruckForm;

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});
